import React, { useState, useEffect, useCallback } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Paper, Grid, Chip, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Skeleton, Divider, IconButton, Tooltip, Alert, Stack,
} from "@mui/material";
import RefreshIcon          from "@mui/icons-material/Refresh";
import CheckCircleIcon      from "@mui/icons-material/CheckCircle";
import ErrorIcon            from "@mui/icons-material/Error";
import MemoryIcon           from "@mui/icons-material/Memory";
import DnsIcon              from "@mui/icons-material/Dns";
import StorageIcon          from "@mui/icons-material/Storage";
import SpeedIcon            from "@mui/icons-material/Speed";
import { isAutheticated }   from "../auth/helper";
import { getServerHealth }  from "./helper/adminapicall";

const StatusChip = ({ ok, label }) => (
  <Chip
    icon={ok
      ? <CheckCircleIcon sx={{ fontSize: "14px !important" }} />
      : <ErrorIcon sx={{ fontSize: "14px !important" }} />}
    label={label || (ok ? "Connected" : "Disconnected")}
    size="small"
    color={ok ? "success" : "error"}
    variant="outlined"
    sx={{ fontSize: "0.72rem" }}
  />
);

const ProgressBar = ({ label, value, max, unit = "MB", color }) => {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const barColor = color || (pct > 85 ? "error" : pct > 65 ? "warning" : "primary");
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" color="text.secondary">
          {value != null ? (typeof value === "number" ? value.toFixed(0) : value) : "—"}{" "}
          {max ? `/ ${typeof max === "number" ? max.toFixed(0) : max} ${unit}` : unit}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate" value={pct}
        color={barColor}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
  );
};

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: "flex", py: 0.75, borderBottom: "1px solid", borderColor: "divider" }}>
    <Typography variant="caption" color="text.secondary" sx={{ width: 170, flexShrink: 0 }}>{label}</Typography>
    <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.78rem" }}>{value ?? "—"}</Typography>
  </Box>
);

const fmtUptime = (seconds) => {
  if (!seconds) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
};

const ServerHealth = () => {
  const { admin, token } = isAutheticated();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [ts,      setTs]      = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError("");
    getServerHealth(admin._id, token)
      .then((d) => {
        if (d?.error) setError(d.error);
        else { setData(d); setTs(new Date()); }
      })
      .catch(() => setError("Failed to fetch server health"))
      .finally(() => setLoading(false));
  }, [admin._id, token]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const node  = data?.node   || {};
  const os    = data?.os     || {};
  const mem   = data?.memory || {};
  const mongo = data?.mongo  || {};
  const redis = data?.redis  || {};
  const pm2   = data?.pm2    || [];
  const disk  = data?.disk   || null;

  const usedMemMB  = mem.used   ? mem.used  / 1048576 : 0;
  const totalMemMB = mem.total  ? mem.total / 1048576 : 0;
  const heapUsedMB = mem.heap?.used  ? mem.heap.used  / 1048576 : 0;
  const heapTotMB  = mem.heap?.total ? mem.heap.total / 1048576 : 0;
  const rssMB      = mem.rss    ? mem.rss   / 1048576 : 0;

  return (
    <Base>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5">Server Health</Typography>
          <Typography variant="body2" color="text.secondary">
            System resources, database and cache status
            {ts && <> · Last updated {ts.toLocaleTimeString()}</>}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>

        {/* ── Service statuses ── */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Service Status</Typography>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DnsIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption">MongoDB</Typography>
                {loading ? <Skeleton width={90} height={24} /> : <StatusChip ok={mongo.connected} label={mongo.connected ? `Connected · v${mongo.version || ""}` : "Disconnected"} />}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <StorageIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption">Redis</Typography>
                {loading ? <Skeleton width={90} height={24} /> : <StatusChip ok={redis.connected} label={redis.connected ? `Connected · v${redis.version || ""}` : "Disconnected"} />}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SpeedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption">Node.js</Typography>
                {loading ? <Skeleton width={80} height={24} /> : <StatusChip ok label={`${node.version || "—"} · lag ${node.eventLoopLag ?? "—"}ms`} />}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* ── Process info ── */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%" }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Process</Typography>
            {loading
              ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={28} sx={{ mb: 0.5 }} />)
              : <>
                  <InfoRow label="Node.js version" value={node.version} />
                  <InfoRow label="Platform"        value={`${os.platform} (${os.arch})`} />
                  <InfoRow label="CPU"             value={`${os.cpuCount}× ${os.cpuModel?.slice(0, 40) || "—"}`} />
                  <InfoRow label="Process uptime"  value={fmtUptime(node.uptime)} />
                  <InfoRow label="OS uptime"       value={fmtUptime(os.uptime)} />
                  <InfoRow label="PID"             value={node.pid} />
                  <InfoRow label="Hostname"        value={os.hostname} />
                  <InfoRow label="Environment"     value={node.env} />
                </>}
          </Paper>
        </Grid>

        {/* ── Memory ── */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <MemoryIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="subtitle2" fontWeight={600}>Memory</Typography>
            </Box>
            {loading
              ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={40} sx={{ mb: 1 }} />)
              : <>
                  <ProgressBar label="System RAM"  value={usedMemMB.toFixed(0)}  max={totalMemMB.toFixed(0)} />
                  <ProgressBar label="Heap Used"   value={heapUsedMB.toFixed(0)} max={heapTotMB.toFixed(0)} />
                  <ProgressBar label="RSS"         value={rssMB.toFixed(0)}      max={totalMemMB.toFixed(0)} />
                  {disk && (
                    <ProgressBar
                      label={`Disk (${disk.mountpoint})`}
                      value={(disk.used / 1073741824).toFixed(1)}
                      max={(disk.total / 1073741824).toFixed(1)}
                      unit="GB"
                    />
                  )}
                </>}
          </Paper>
        </Grid>

        {/* ── Load averages ── */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Load Averages</Typography>
            {loading ? (
              <Skeleton height={60} />
            ) : (
              <Box sx={{ display: "flex", gap: 4 }}>
                {[["1m", os.loadavg?.["1m"]], ["5m", os.loadavg?.["5m"]], ["15m", os.loadavg?.["15m"]]].map(([label, val]) => (
                  <Box key={label} sx={{ textAlign: "center" }}>
                    <Typography variant="h5" fontWeight={700}>{val != null ? val.toFixed(2) : "—"}</Typography>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── Redis ── */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <StorageIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="subtitle2" fontWeight={600}>Redis</Typography>
              {!loading && <StatusChip ok={redis.connected} />}
            </Box>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={28} sx={{ mb: 0.5 }} />)
            ) : redis.connected ? (
              <>
                {redis.maxMemory > 0 && (
                  <ProgressBar
                    label="Memory used"
                    value={(redis.usedMemory / 1048576).toFixed(1)}
                    max={(redis.maxMemory / 1048576).toFixed(1)}
                  />
                )}
                <InfoRow label="Memory used"        value={redis.usedMemoryHuman} />
                <InfoRow label="Total keys"         value={redis.totalKeys?.toLocaleString()} />
                <InfoRow label="Connected clients"  value={redis.clients} />
                <InfoRow label="Uptime"             value={fmtUptime(redis.uptime)} />
                <InfoRow label="Mode / Role"        value={`${redis.mode} / ${redis.role}`} />
                {redis.hitRate != null && (
                  <InfoRow label="Cache hit rate" value={`${redis.hitRate}%`} />
                )}
                <InfoRow label="Commands processed" value={redis.totalCommandsProcessed?.toLocaleString()} />
              </>
            ) : (
              <Alert severity="error" sx={{ mt: 1 }}>{redis.error || "Redis not available"}</Alert>
            )}
          </Paper>
        </Grid>

        {/* ── MongoDB detail ── */}
        {!loading && mongo.connected && (
          <>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <DnsIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  <Typography variant="subtitle2" fontWeight={600}>MongoDB</Typography>
                  <StatusChip ok={mongo.connected} />
                </Box>
                <InfoRow label="Version"         value={mongo.version} />
                <InfoRow label="Connections"     value={`${mongo.connections?.current ?? "—"} current / ${mongo.connections?.available ?? "—"} available`} />
                <InfoRow label="DB uptime"       value={fmtUptime(mongo.uptime)} />
                <InfoRow label="Data size"       value={mongo.dbStats?.dataSizeFmt} />
                <InfoRow label="Storage size"    value={mongo.dbStats?.storageSizeFmt} />
                <InfoRow label="Index size"      value={mongo.dbStats?.indexSizeFmt} />
                <InfoRow label="Collections"     value={mongo.dbStats?.collections} />
                <InfoRow label="Total documents" value={mongo.dbStats?.objects?.toLocaleString()} />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Opcounters (since startup)</Typography>
                {["insert", "query", "update", "delete", "getmore", "command"].map(op => (
                  <InfoRow key={op} label={op.charAt(0).toUpperCase() + op.slice(1)} value={mongo.opcounters?.[op]?.toLocaleString()} />
                ))}
              </Paper>
            </Grid>
          </>
        )}

        {/* ── Collections table ── */}
        {!loading && (mongo.collections || []).length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle2" fontWeight={600}>Collection Stats</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Collection", "Documents", "Size", "Avg obj size"].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mongo.collections.map(col => (
                      <TableRow key={col.name} hover>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{col.name}</TableCell>
                        <TableCell>{col.count?.toLocaleString() ?? "—"}</TableCell>
                        <TableCell>{col.sizeFmt ?? "—"}</TableCell>
                        <TableCell>{col.avgObjSize != null ? `${col.avgObjSize} B` : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* ── PM2 processes ── */}
        {(loading || pm2.length > 0) && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle2" fontWeight={600}>PM2 Processes</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Name", "PID", "Status", "CPU", "Memory", "Restarts", "Uptime"].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading
                      ? Array.from({ length: 3 }).map((_, i) => (
                          <TableRow key={i}>
                            {Array.from({ length: 7 }).map((__, j) => (
                              <TableCell key={j}><Skeleton height={20} /></TableCell>
                            ))}
                          </TableRow>
                        ))
                      : pm2.map((p) => (
                          <TableRow key={p.pid} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{p.name}</TableCell>
                            <TableCell sx={{ color: "text.secondary" }}>{p.pid}</TableCell>
                            <TableCell>
                              <Chip
                                label={p.status}
                                size="small"
                                color={p.status === "online" ? "success" : "error"}
                                sx={{ fontSize: "0.7rem" }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: "text.secondary" }}>{p.cpu ?? "—"}%</TableCell>
                            <TableCell sx={{ color: "text.secondary" }}>{p.memFmt ?? "—"}</TableCell>
                            <TableCell sx={{ color: "text.secondary" }}>{p.restarts ?? "—"}</TableCell>
                            <TableCell sx={{ color: "text.secondary" }}>
                              {p.uptime != null ? `${Math.floor(p.uptime / 60000)}m` : "—"}
                            </TableCell>
                          </TableRow>
                        ))
                    }
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

      </Grid>
    </Base>
  );
};

export default ServerHealth;
